/* tslint:disable:no-unused-variable member-ordering */
import { Inject, Injectable, Optional } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpEvent, HttpParameterCodec } from '@angular/common/http'
import { Observable } from 'rxjs'

import { FileUploadResponse } from '../model/models'

import { BASE_PATH } from '../variables'
import { Configuration } from '../configuration'

export interface UploadFileRequestParams {
    file?: File
    fileName?: string
}

@Injectable({
    providedIn: 'root',
})
export class FilesAPIService {
    protected basePath = 'http://localhost:8080'
    public defaultHeaders = new HttpHeaders()
    public configuration = new Configuration()
    public encoder: HttpParameterCodec

    constructor(
        protected httpClient: HttpClient,
        @Optional() @Inject(BASE_PATH) basePath: string,
        @Optional() configuration: Configuration
    ) {
        if (configuration) {
            this.configuration = configuration
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (typeof basePath !== 'string') {
                basePath = this.basePath
            }
            this.configuration.basePath = basePath
        }
    }

    /**
     * @param consumes string[] mime-types
     * @return true: consumes contains 'multipart/form-data', false: otherwise
     */
    private canConsumeForm(consumes: string[]): boolean {
        const form = 'multipart/form-data'
        for (const consume of consumes) {
            if (form === consume) {
                return true
            }
        }
        return false
    }

    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === 'object' && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value)
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key)
        }
        return httpParams
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams
        }

        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                ;(value as any[]).forEach((elem) => (httpParams = this.addToHttpParamsRecursive(httpParams, elem, key)))
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key, (value as Date).toISOString().substr(0, 10))
                } else {
                    throw Error('key may not be null if value is Date')
                }
            } else {
                Object.keys(value).forEach(
                    (k) =>
                        (httpParams = this.addToHttpParamsRecursive(
                            httpParams,
                            value[k],
                            key != null ? `${key}.${k}` : k
                        ))
                )
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value)
        } else {
            throw Error('key may not be null if value is not object or array')
        }
        return httpParams
    }

    /**
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public uploadFile(
        requestParameters: UploadFileRequestParams,
        observe?: 'body',
        reportProgress?: boolean,
        options?: { httpHeaderAccept?: 'application/json' }
    ): Observable<FileUploadResponse>
    public uploadFile(
        requestParameters: UploadFileRequestParams,
        observe?: 'response',
        reportProgress?: boolean,
        options?: { httpHeaderAccept?: 'application/json' }
    ): Observable<HttpResponse<FileUploadResponse>>
    public uploadFile(
        requestParameters: UploadFileRequestParams,
        observe?: 'events',
        reportProgress?: boolean,
        options?: { httpHeaderAccept?: 'application/json' }
    ): Observable<HttpEvent<FileUploadResponse>>
    public uploadFile(
        requestParameters: UploadFileRequestParams,
        observe: any = 'body',
        reportProgress: boolean = false,
        options?: { httpHeaderAccept?: 'application/json' }
    ): Observable<any> {
        const file = requestParameters.file
        const fileName = requestParameters.fileName

        let headers = this.defaultHeaders

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = ['application/json']
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts)
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected)
        }

        // to determine the Content-Type header
        const consumes: string[] = ['multipart/form-data']

        const canConsumeForm = this.canConsumeForm(consumes)

        const formParams = new FormData()
        console.log(`params ${file}`)
        if (file !== undefined) {
            formParams.append('file', file, file.name)
        }
        if (fileName !== undefined) {
            formParams.append('fileName', <any>fileName)
        }

        let responseType: 'text' | 'json' = 'json'
        if (httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text'
        }

        return this.httpClient.post<FileUploadResponse>(`${this.configuration.basePath}/files`, formParams, {
            responseType: <any>responseType,
            withCredentials: this.configuration.withCredentials,
            headers: headers,
            observe: observe,
            reportProgress: reportProgress,
        })
    }
}
